import { plus } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { getAsset } from '../../utils/getAsset'
import { binanceApiEndpoint, binanceSymbolCryptoIdMap } from '../constants'
import { coinInfoSchema } from './schema'

const responseSchema = z.array(coinInfoSchema)

const urlPath = '/sapi/v1/capital/config/getall'

type FetchSpotAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchSpotAccountAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchSpotAccountAssetsParameters): Promise<readonly AssetBalance[]> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, binanceApiEndpoint)
  url.searchParams.append('timestamp', timestamp.toString())
  url.searchParams.append(
    'signature',
    await createSha256Hash(url.searchParams.toString(), apiSecret),
  )

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          'X-MBX-APIKEY': apiKey,
          ...proxyHeaders,
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response) {
    const asset = getAsset({
      symbol: assetData.coin,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    const amount = plus(assetData.free, assetData.locked)
    if (amount.value <= 0n) {
      continue
    }
    assetBalances.push({
      key: 'binance_spot',
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount: plus(assetData.free, assetData.locked),
      timestamp,
    })
  }

  return assetBalances
}
